import { useState } from "react";
import { CheckCircle2, Loader2 } from "lucide-react";
import { useT } from "../i18n";
import { PageHero } from "../components/kit";
import { ContactAside } from "./Pages";

const API = process.env.REACT_APP_BACKEND_URL;
const CATS = ["general", "technical", "billing", "account", "abuse", "feedback"];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const empty = { name: "", email: "", category: "", plate: "", message: "", consent: false, website: "" };

const Field = ({ id, label, error, children }) => (
  <div>
    <label htmlFor={id} className="mb-2 block text-sm font-medium">{label}</label>
    {children}
    {error && <p id={`${id}-err`} className="mt-1.5 text-sm text-danger" data-testid={`contact-error-${id}`}>{error}</p>}
  </div>
);

export default function Contact() {
  const { lang, t } = useT();
  const c = t.contact;
  const f = c.form;
  const [v, setV] = useState(empty);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const set = (k) => (e) => setV((s) => ({ ...s, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const validate = () => {
    const e = {};
    if (v.name.trim().length < 2) e.name = c.errors.name;
    if (!EMAIL_RE.test(v.email.trim())) e.email = c.errors.email;
    if (!v.category) e.category = c.errors.category;
    if (v.message.trim().length < 10) e.message = c.errors.message;
    if (!v.consent) e.consent = c.errors.consent;
    return e;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length) return;
    if (!API) { setStatus("error"); return; }
    setStatus("sending");
    try {
      const res = await fetch(`${API}/api/contact`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ...v, lang }) });
      if (!res.ok) throw new Error(String(res.status));
      setStatus("sent");
      setV(empty);
    } catch {
      setStatus("error");
    }
  };

  const input = (id) => `h-12 w-full rounded-xl border bg-surface px-4 text-base outline-none transition-colors focus:border-primary ${errors[id] ? "border-danger" : "border-input"}`;
  const aria = (id) => ({ id, "aria-invalid": !!errors[id], "aria-describedby": errors[id] ? `${id}-err` : undefined, "data-testid": `contact-input-${id}` });

  return (
    <>
      <PageHero eyebrow={c.eyebrow} lines={c.h1} lead={c.lead} testid="contact-hero" />
      <section className="container-x grid gap-14 pb-24 lg:grid-cols-[1.4fr_1fr]">
        {status === "sent" ? (
          <div className="card p-10" role="status" data-testid="contact-success">
            <CheckCircle2 className="h-10 w-10 text-success" aria-hidden />
            <h2 className="mt-6 text-3xl">{c.success.t}</h2>
            <p className="mt-3 max-w-lg text-muted-foreground">{c.success.d}</p>
            <button type="button" className="btn-secondary mt-8" onClick={() => setStatus("idle")} data-testid="contact-send-another">{c.success.again}</button>
          </div>
        ) : (
          <form noValidate onSubmit={submit} className="card space-y-6 p-6 md:p-10" data-testid="contact-form">
            <div className="grid gap-6 md:grid-cols-2">
              <Field id="name" label={f.name} error={errors.name}><input {...aria("name")} autoComplete="name" value={v.name} onChange={set("name")} className={input("name")} /></Field>
              <Field id="email" label={f.email} error={errors.email}><input {...aria("email")} type="email" autoComplete="email" value={v.email} onChange={set("email")} className={input("email")} /></Field>
            </div>
            <div className="grid gap-6 md:grid-cols-2">
              <Field id="category" label={f.category} error={errors.category}>
                <select {...aria("category")} value={v.category} onChange={set("category")} className={input("category")}>
                  <option value="">{f.choose}</option>
                  {CATS.map((k) => <option key={k} value={k}>{c.cats[k]}</option>)}
                </select>
              </Field>
              <Field id="plate" label={f.plate}><input {...aria("plate")} value={v.plate} onChange={set("plate")} placeholder={f.platePh} className={`${input("plate")} font-plate font-semibold uppercase tracking-[0.08em]`} maxLength={20} /></Field>
            </div>
            <Field id="message" label={f.message} error={errors.message}>
              <textarea {...aria("message")} rows={6} value={v.message} onChange={set("message")} maxLength={4000} className={`${input("message")} h-auto py-3`} />
            </Field>
            <input type="text" name="website" tabIndex={-1} autoComplete="off" value={v.website} onChange={set("website")} className="hidden" aria-hidden />
            <div>
              <label className="flex items-start gap-3 text-sm">
                <input type="checkbox" checked={v.consent} onChange={set("consent")} className="mt-1 h-4 w-4 accent-[hsl(var(--primary))]" data-testid="contact-input-consent" aria-invalid={!!errors.consent} />
                <span>{f.consent}</span>
              </label>
              {errors.consent && <p className="mt-1.5 text-sm text-danger" data-testid="contact-error-consent">{errors.consent}</p>}
            </div>
            {status === "error" && <p role="alert" className="rounded-xl border border-danger/50 bg-danger/10 p-4 text-sm" data-testid="contact-error-server">{c.errors.server}</p>}
            <button type="submit" className="btn-primary h-12 px-8 disabled:cursor-not-allowed disabled:opacity-60" disabled={status === "sending"} data-testid="contact-submit-btn">
              {status === "sending" && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
              {status === "sending" ? f.sending : f.submit}
            </button>
          </form>
        )}
        <ContactAside />
      </section>
    </>
  );
}
